import { useNavigate } from "react-router-dom";
import { Alert, Box, Button, Chip, Stack, Typography } from "@mui/material";
import { formatMoney, formatDate } from "@bw/shared/utils";
import type { SubscriptionResponse } from "@bw/shared/types/generated";
import { Page } from "../../../common/components/Page";
import { planTypeDisplay } from "../lib/planDisplay";
import { deriveSubscriptionView } from "../lib/subscriptionState";

/**
 * Purchase confirmation — renders the REAL subscribe result (SubscriptionResponse)
 * in-place after a successful POST /memberships/subscribe. Every fact is from the
 * response: the derived status, the next renewal from current_period_end, and —
 * for a visits_card pass — the starting visit allotment from the enriched
 * visit_limit. No marketing copy, no fabricated numbers.
 *
 * `renews_at` is ignored (always null); the period boundary is
 * current_period_end. An incomplete first-charge subscription renders honestly
 * via deriveSubscriptionView ("Activating") rather than over-claiming "Active".
 */
export function PurchaseConfirmation({
  subscription,
  facilityId,
}: {
  subscription: SubscriptionResponse;
  facilityId: string;
}) {
  const navigate = useNavigate();
  const view = deriveSubscriptionView(subscription);
  const display = subscription.plan_type
    ? planTypeDisplay(subscription.plan_type, subscription.billing_interval)
    : null;
  const isPass = display?.framing === "pass";
  const planName = subscription.plan_name?.trim() || "Your membership";
  const periodEnd = subscription.current_period_end;

  // Honest headline: bind it to the DERIVED state, never a hard-coded "You're
  // all set". A cleared charge (active/ending/past_due → hasAccess) celebrates;
  // an unconfirmed first charge (status='incomplete' → deriveSubscriptionView
  // "Activating") is still processing and must NOT claim success — Stripe's
  // default first-charge behavior can return HTTP 200 with an incomplete
  // (SCA/soft-declined) subscription where the money has not moved.
  const activating = !view.hasAccess && subscription.status === "incomplete";
  const title = view.hasAccess
    ? "You're all set"
    : activating
      ? "Payment processing"
      : "Something needs your attention";

  return (
    <Page>
      <Stack spacing={3} data-testid="purchase-confirmation">
        <Stack spacing={0.75}>
          <Typography component="h1" variant="h4">
            {title}
          </Typography>
          <Typography color="text.secondary">
            {planName}
            {subscription.facility_name?.trim()
              ? ` · ${subscription.facility_name.trim()}`
              : ""}
          </Typography>
          {!view.hasAccess ? (
            <Alert severity={activating ? "info" : "warning"} role="status">
              {activating
                ? "Almost there — we're confirming your first payment with your bank. Your membership starts the moment it clears."
                : "We couldn't confirm your membership yet. Please try again from your memberships."}
            </Alert>
          ) : null}
        </Stack>

        <Box component="dl" sx={{ m: 0 }}>
          <ConfirmationFact
            label="Status"
            value={<Chip size="small" label={view.label} variant="outlined" />}
          />

          {subscription.price != null ? (
            <ConfirmationFact
              label="Price"
              value={
                <>
                  {formatMoney(subscription.price)}
                  {display?.priceSuffix ?? ""}
                </>
              }
            />
          ) : null}

          {/* Next renewal — bound to current_period_end (never renews_at). Omitted
              gracefully when the backend hasn't set a period yet. */}
          {periodEnd ? (
            <ConfirmationFact
              label={view.tone === "ending" ? "Cancels" : "Renews"}
              value={formatDate(periodEnd)}
            />
          ) : null}

          {/* Starting pass allotment — the enriched visit_limit, straight from
              the response. Only for a visits_card pass whose first charge has
              cleared (visits aren't minted until the subscription is active). */}
          {view.hasAccess && isPass && subscription.visit_limit != null ? (
            <ConfirmationFact
              label="Included visits"
              value={
                <>
                  {subscription.visit_limit}{" "}
                  {subscription.visit_limit === 1 ? "visit" : "visits"} to start
                </>
              }
            />
          ) : null}
        </Box>

        <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
          <Button
            variant="contained"
            size="small"
            onClick={() => navigate("/me/memberships")}
          >
            View my memberships
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={() => navigate(`/book/${facilityId}`)}
          >
            Book a court
          </Button>
        </Stack>
      </Stack>
    </Page>
  );
}

function ConfirmationFact({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) {
  return (
    <Stack
      direction="row"
      spacing={2}
      sx={{
        py: 1.25,
        borderBottom: 1,
        borderColor: "divider",
        justifyContent: "space-between",
        alignItems: "center",
      }}
    >
      <Typography component="dt" color="text.secondary">
        {label}
      </Typography>
      <Typography component="dd" sx={{ m: 0, fontWeight: 600 }}>
        {value}
      </Typography>
    </Stack>
  );
}
